<script setup>
import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import useValidation from "@/utils/helpers/validations";
import useEncryption from "@/utils/helpers/encryption";
import { useToast } from "vue-toastification";
import AuthLayout from "@/views/layouts/AuthLayout.vue";
import AuthHeroPanel from "@/components/auth/AuthHeroPanel.vue";

const toast = useToast();
const { encryptionPassword } = useEncryption();
const { checkEmailValidation } = useValidation();
const authStore = useAuthStore();

const userForm = ref({
  first_name: "",
  last_name: "",
  email: "",
  company: "",
  phone: "",
  password: "",
  password_confirmation: "",
});

let isValidEmail = ref(true);
let isRegisterError = ref(false);
let isSubmitted = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
let subscriptionLink = ref(null);

const validEmail = () => {
  isRegisterError.value = false;
  isValidEmail.value = checkEmailValidation(userForm.value.email);
};

const onSubmit = async () => {
  if (userForm.value.password == userForm.value.password_confirmation) {
    userForm.value.password = await encryptionPassword(userForm.value.password);
    userForm.value.password_confirmation = userForm.value.password;
  } else {
    return toast.error("Passwords must be same");
  }
  authStore
    .register(userForm.value)
    .then(() => {})
    .catch(() => {
      isRegisterError.value = true;
      isSubmitted.value = false;
    });
};
</script>

<template>
  <AuthLayout variant="split">
    <div
      class="register-split"
      @keyup.enter="!(
          !userForm.first_name || !userForm.last_name || !userForm.email ||
          !userForm.company || !userForm.password || isSubmitted || !isValidEmail
        ) && onSubmit()"
    >
      <!-- Form panel -->
      <section class="register-form-panel">
        <div class="register-form-panel__inner">
          <div class="auth-logo-wrap register-brand">
            <img class="auth-logo-img" src="@/assets/images/logo/hiddenox.svg" alt="Ravo" />
            <span class="auth-logo-text">Ravo</span>
          </div>

          <div class="register-head">
            <h1 class="register-head__title">Create your account</h1>
            <p class="register-head__subtitle">Join Ravo to manage your agents, devices, and conversations.</p>
          </div>

          <!-- First name -->
          <div class="auth-field-wrap">
            <span class="p-float-label auth-float">
              <PInput
                id="reg-first-name"
                :model-value="userForm.first_name"
                @input="userForm.first_name = $event.target.value; isRegisterError = false"
                type="text"
                :class="{ 'p-invalid': isRegisterError }"
                class="auth-pinput"
              />
              <label for="reg-first-name">First Name</label>
            </span>
          </div>

          <!-- Last name -->
          <div class="auth-field-wrap">
            <span class="p-float-label auth-float">
              <PInput
                id="reg-last-name"
                :model-value="userForm.last_name"
                @input="userForm.last_name = $event.target.value; isRegisterError = false"
                type="text"
                :class="{ 'p-invalid': isRegisterError }"
                class="auth-pinput"
              />
              <label for="reg-last-name">Last Name</label>
            </span>
          </div>

          <!-- Email -->
          <div class="auth-field-wrap">
            <span class="p-float-label auth-float">
              <PInput
                id="reg-email"
                :model-value="userForm.email"
                @input="userForm.email = $event.target.value; validEmail()"
                type="email"
                :class="{ 'p-invalid': !isValidEmail || isRegisterError }"
                class="auth-pinput"
                autocomplete="email"
              />
              <label for="reg-email">Email Address</label>
            </span>
            <small v-if="!isValidEmail" class="auth-field-error">Invalid email address.</small>
          </div>

          <!-- Company -->
          <div class="auth-field-wrap">
            <span class="p-float-label auth-float">
              <PInput
                id="reg-company"
                :model-value="userForm.company"
                @input="userForm.company = $event.target.value; isRegisterError = false"
                type="text"
                :class="{ 'p-invalid': isRegisterError }"
                class="auth-pinput"
              />
              <label for="reg-company">Company Name</label>
            </span>
          </div>

          <!-- Phone -->
          <div class="auth-field-wrap">
            <span class="p-float-label auth-float">
              <PInput
                id="reg-phone"
                :model-value="userForm.phone"
                @input="userForm.phone = $event.target.value"
                type="text"
                class="auth-pinput"
              />
              <label for="reg-phone">Phone Number (Optional)</label>
            </span>
          </div>

          <!-- Password -->
          <div class="auth-field-wrap auth-field-wrap--password">
            <span class="p-float-label auth-float">
              <PInput
                id="reg-password"
                :model-value="userForm.password"
                @input="userForm.password = $event.target.value; isRegisterError = false"
                :type="showPassword ? 'text' : 'password'"
                :class="{ 'p-invalid': isRegisterError }"
                class="auth-pinput"
                autocomplete="new-password"
              />
              <label for="reg-password">Password</label>
            </span>
            <button type="button" class="auth-eye-btn" @click="showPassword = !showPassword" tabindex="-1">
              <i :class="showPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
            </button>
          </div>

          <!-- Confirm Password -->
          <div class="auth-field-wrap auth-field-wrap--password">
            <span class="p-float-label auth-float">
              <PInput
                id="reg-confirm-password"
                :model-value="userForm.password_confirmation"
                @input="userForm.password_confirmation = $event.target.value; isRegisterError = false"
                :type="showConfirmPassword ? 'text' : 'password'"
                :class="{ 'p-invalid': isRegisterError }"
                class="auth-pinput"
                autocomplete="new-password"
              />
              <label for="reg-confirm-password">Confirm Password</label>
            </span>
            <button type="button" class="auth-eye-btn" @click="showConfirmPassword = !showConfirmPassword" tabindex="-1">
              <i :class="showConfirmPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
            </button>
          </div>

          <button
            @click="onSubmit"
            :disabled="
              !userForm.first_name || !userForm.last_name || !userForm.email ||
              !userForm.company || !userForm.password || isSubmitted || !isValidEmail
            "
            type="button"
            class="auth-submit-btn"
          >
            REGISTER
            <span v-if="isSubmitted" class="spinner-sm" role="status" aria-hidden="true"></span>
          </button>

          <p class="forth-welcome-message" style="margin-top: 20px;">Already have an account?</p>
          <router-link class="auth-link" to="/login" style="display:block; text-align:center; margin-top:8px;">
            LOGIN
          </router-link>

          <div v-html="subscriptionLink"></div>
        </div>
      </section>

      <!-- Brand hero panel -->
      <AuthHeroPanel
        tag="Get Started"
        title-line="Build your"
        title-emphasis="AI workforce."
        description="Create a Ravo account to deploy agents, connect data sources, and launch campaigns across every site."
        :features="[
          'Deploy agents to any kiosk or site',
          'Connect your own data sources',
          'Launch and track campaigns in real time',
        ]"
      />
    </div>
  </AuthLayout>
</template>

<style scoped>
.register-split {
  min-height: 100vh;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
}

/* ── Form panel ─────────────────────────────────────────────────── */
.register-form-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  background: #f4f6fb;
  transition: background 0.3s;
}

.register-form-panel__inner {
  width: 100%;
  max-width: 384px;
}

.register-brand {
  justify-content: flex-start;
  margin-bottom: 32px;
}

.register-head {
  margin-bottom: 24px;
}

.register-head__title {
  margin: 0 0 8px;
  font-family: "lato-black", Lato, sans-serif;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
  text-wrap: balance;
}

.register-head__subtitle {
  margin: 0;
  font-family: Lato, sans-serif;
  font-size: 14.5px;
  color: #64748b;
  line-height: 1.55;
}

/* ── Centered submit button ────────────────────────────────────── */
.register-form-panel .auth-submit-btn {
  width: auto;
  min-width: 200px;
  margin-left: auto;
  margin-right: auto;
}

/* ── Eye toggle flush to the input's end ─────────────────────────── */
.register-form-panel .auth-eye-btn {
  right: 4px;
}

/* ── Dark mode ──────────────────────────────────────────────────── */
[data-theme="dark"] .register-form-panel {
  background: #0d1117;
}

[data-theme="dark"] .register-head__title {
  color: #e8edf5;
}

[data-theme="dark"] .register-head__subtitle {
  color: #9aadc4;
}

/* ── Responsive: hero collapses, form goes single column ─────────── */
@media (max-width: 900px) {
  .register-split {
    grid-template-columns: 1fr;
  }

  .register-form-panel {
    padding: 40px 22px;
  }
}
</style>
